import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  AreaEnrollmentChart,
  BarDemographicsChart, AreaYearlyChart, BarStatisticsChart, BarRankingChart, PALETTE,
} from './charts'
import { Card } from '@/shared/ui/Card'
import { PaymentMethodRevenue } from './PaymentMethodRevenue'
import { AnalyticsBreakdown } from './AnalyticsBreakdown'
import { HeatMap } from './HeatMap'
import { UpcomingSchedules } from './UpcomingSchedules'
import { Avatar } from '@/shared/ui/Avatar'
import { StatusBadge } from '@/shared/ui/Badge'
import { Spinner } from '@/shared/ui/Spinner'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { toast } from '@/store/useToastStore'
import { useSchoolCan } from '@/shared/permissions'
import { useDashboardStats, useSchoolDashboardStats, usePendingVerifications, useUpdateSessionStatusOnBehalf } from '@/api/hooks'
import { dashboardService } from '@/api/services/dashboardService'
import { bookingService } from '@/api/services/bookingService'
import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { useAuthStore } from '@/store/useAuthStore'
import { formatCurrency } from '@/shared/utils'
import { getPaymentMethodLabel } from '@/shared/constants'
import { Calendar, BookOpen, Check, X, Clock, Wallet, Star, Building2, CalendarClock, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  TrainersIcon, StudentsIcon, FailedTransactionIcon, CancelledBookingIcon,
  CompletedBookingIcon, PendingCertificateIcon, RescheduledBookingIcon, SchoolsIcon, RefundIcon,
} from './analyticsIcons'

// ── helpers ──────────────────────────────────────────────────────────────────
function fmt(num: number | undefined | null) {
  if (!num) return '0'
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B'
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M'
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K'
  return num.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// snake_case / camelCase → "Title Case"
function humanize(s: string) {
  return String(s)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

// The API concatenates multiple session comments into one string with "|"
// separators (and sometimes nothing but empty segments, e.g. "|||||||").
// Split, trim and drop the empties so we only show real comments.
function cleanFeedbackComments(raw?: string) {
  if (!raw) return ''
  return raw.split('|').map(s => s.trim()).filter(Boolean).join(' · ')
}

function Stars({ n }: { n: number }) {
  return <span className="text-brand-400 text-sm">{Array.from({length:5},(_,i)=>i<Math.floor(n)?'★':'☆').join('')}</span>
}

// ── Sub-components ─────────────────────────────────────────────────────────
function DashboardCard({ link, gradient, iconGradient, value, exactValue, label, hoverLines, icon, onClick }: {
  link?: string; gradient: string; iconGradient: string; value: string|number
  exactValue?: number; label: string; hoverLines?: { label: string; value: string|number }[]; icon?: string; onClick?: ()=>void
}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <div
      onClick={() => link ? navigate(link) : onClick?.()}
      title={exactValue ? t('dash.exactTooltip', { value: exactValue.toLocaleString() }) : ''}
      className="group relative cursor-pointer rounded-xl p-4 flex items-center justify-between gap-3 hover:opacity-90 transition-opacity"
      style={{ background: gradient }}
    >
      {hoverLines && (
        <div className="pointer-events-none absolute left-0 bottom-full z-30 mb-1 hidden w-max min-w-[11rem] max-w-[calc(100vw-2rem)] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          {hoverLines.map(l => (
            <div key={l.label} className="flex items-center justify-between gap-4 py-0.5">
              <span className="text-gray-300">{l.label}</span>
              <span className="font-bold">{l.value}</span>
            </div>
          ))}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
        <div className="text-[13px] text-gray-500 uppercase tracking-wide mt-0.5 font-semibold">{label}</div>
      </div>
      <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden" style={{ background: iconGradient }}>
        {icon ? <img src={icon} alt={label} className="w-full h-full object-cover"/> : <Calendar size={18} className="text-white"/>}
      </div>
    </div>
  )
}

function AnalyticsCard({ link, linkState, bgColor, value, label, hoverLines, icon, onClick }: {
  link?: string; linkState?: unknown; bgColor: string; value: string|number; label: string
  hoverLines?: { label: string; value: string|number }[]; icon?: React.ReactNode; onClick?: ()=>void
}) {
  const navigate = useNavigate()
  return (
    <div
      onClick={() => link ? navigate(link, { state: linkState }) : onClick?.()}
      className="group relative cursor-pointer rounded-xl p-3.5 flex items-center justify-between gap-3 hover:opacity-90 transition-opacity"
      style={{ background: bgColor }}
    >
      <div className="min-w-0">
        <div className="text-xl font-bold text-gray-900">{value}</div>
        <div className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mt-0.5 leading-tight break-words">{label}</div>
      </div>
      {hoverLines && (
        <div className="pointer-events-none absolute left-0 bottom-full z-30 mb-1 hidden w-max min-w-[11rem] max-w-[calc(100vw-2rem)] rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          {hoverLines.map(l => (
            <div key={l.label} className="flex items-center justify-between gap-4 py-0.5">
              <span className="text-gray-300">{l.label}</span>
              <span className="font-bold">{l.value}</span>
            </div>
          ))}
        </div>
      )}
      {icon && <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">{icon}</div>}
    </div>
  )
}

function SectionHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {children}
    </div>
  )
}

function CardShell({ title, subheader, action, children, className }: {
  title?: string; subheader?: string; action?: React.ReactNode; children: React.ReactNode; className?: string
}) {
  return (
    <Card className={className}>
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            {title && <div className="font-bold text-gray-900">{title}</div>}
            {subheader && <div className="text-xs text-gray-400 mt-0.5">{subheader}</div>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Card>
  )
}


function TabSelector({ options, value, onChange }: { options: string[]; value: string; onChange: (v:string)=>void }) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-1">
      {options.map(o => (
        <button key={o} onClick={()=>onChange(o)}
          className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${o===value?'bg-brand-500 text-white border-brand-500':'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
          {t(`dash.${o.toLowerCase()}`, o)}
        </button>
      ))}
    </div>
  )
}

// ── MAIN DASHBOARD ─────────────────────────────────────────────────────────
export function DashboardPage() {
  const { t } = useTranslation()
  // Localized short month labels for chart axes (falls back to English MONTHS).
  const months = (t('dash.monthsShort', { returnObjects: true }) as string[]) ?? MONTHS
  const user = useAuthStore(s => s.user)
  const isSchoolAdmin = user?.user_type === 'school_admin'
  // School admins only see the dashboard blocks their roles allow (no-op for platform admins).
  const schoolCan = useSchoolCan()
  const navigate = useNavigate()

  // Analytics state
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const appliedFilters = { startDate, endDate }
  // City filter applies instantly on click (no Apply button) and drives every admin card/chart
  const [cityId, setCityId] = useState<number|null>(null)
  const [cities, setCities] = useState<any[]>([])

  // Chart period state
  const [certSeries, setCertSeries] = useState('Monthly')
  const [sessionSeries, setSessionSeries] = useState('Monthly')
  const [revYear, setRevYear] = useState(String(new Date().getFullYear()))
  const [sessionsMonthFilter, setSessionsMonthFilter] = useState('all')

  // Real API data — admin uses admin analytics, school admin uses school analytics
  const adminStatsQ = useDashboardStats({
    start_date: appliedFilters.startDate || undefined,
    end_date: appliedFilters.endDate || undefined,
    city_id: cityId ?? undefined,
  })
  const schoolStatsQ = useSchoolDashboardStats({
    start_date: appliedFilters.startDate || undefined,
    end_date: appliedFilters.endDate || undefined,
  })
  const analyticsRaw     = isSchoolAdmin ? schoolStatsQ.data : adminStatsQ.data
  const analyticsLoading = isSchoolAdmin ? schoolStatsQ.isLoading : adminStatsQ.isLoading
  const { data: verifyRaw, isLoading: verifyLoading, refetch: refetchVerifications } = usePendingVerifications({ page: 1, limit: 15, sort_by: 'desc', is_verified: 0 })
  // id of the request currently being accepted/rejected, with which action
  const [verifyingId, setVerifyingId] = useState<{ id: any; action: 'accept' | 'reject' } | null>(null)
  // request pending confirmation in the modal (null = closed)
  const [verifyTarget, setVerifyTarget] = useState<{ request: any; accept: boolean } | null>(null)

  // Booking session requests — school admin only (mirrors /school/booking/session-requests)
  const [sessionReqPage, setSessionReqPage] = useState(1)
  const sessionReqPerPage = 5
  const sessionRequestsQ = useQuery({
    queryKey: ['dashboard-session-requests'],
    queryFn: () => bookingService.sessionRequests({ page: 1, limit: 100 }),
    enabled: isSchoolAdmin,
    staleTime: 30_000,
  })
  // Drop requests whose scheduled time has already passed — the dashboard only
  // surfaces ones the school admin can still act on. (The full page still lists them.)
  const sessionRequestsActionable: any[] = ((sessionRequestsQ.data as any)?.data ?? []).filter((r: any) => {
    const ms = r.start_time ? new Date(r.start_time).getTime() : NaN
    return !(Number.isFinite(ms) && ms < Date.now())
  })
  const sessionRequestsTotal = sessionRequestsActionable.length
  const sessionReqLastPage = Math.ceil(sessionRequestsTotal / sessionReqPerPage) || 1
  // Client-side page slice — keeps counts accurate after the passed-time filter.
  const sessionRequests: any[] = sessionRequestsActionable.slice(
    (sessionReqPage - 1) * sessionReqPerPage,
    sessionReqPage * sessionReqPerPage,
  )
  // Keep the page valid when the actionable count shrinks (e.g. after accepting one).
  useEffect(() => {
    if (sessionReqPage > sessionReqLastPage) setSessionReqPage(sessionReqLastPage)
  }, [sessionReqLastPage, sessionReqPage])
  const acceptSessionRequest = useUpdateSessionStatusOnBehalf()
  const [acceptingSessionId, setAcceptingSessionId] = useState<number | null>(null)

  const handleAcceptSessionRequest = (sessionId: number) => {
    setAcceptingSessionId(sessionId)
    acceptSessionRequest.mutate(
      { sessions_id: sessionId, session_status: 1 },
      {
        onSuccess: () => { toast.success(t('dash.sessionAccepted')); sessionRequestsQ.refetch() },
        onError: (e: any) => toast.error(e?.response?.data?.message ?? t('dash.sessionAcceptFailed')),
        onSettled: () => setAcceptingSessionId(null),
      },
    )
  }

  // Revenue data
  const [revenueData, setRevenueData] = useState<any>(null)
  const [revenueLoading, setRevenueLoading] = useState(false)
  const [insightsLoading, setInsightsLoading] = useState(true)
  const [trainerInsights, setTrainerInsights] = useState<any>(null)
  const [studentInsights, setStudentInsights] = useState<any>(null)

  useEffect(() => {
    const revenueP = isSchoolAdmin
      ? dashboardService.schoolRevenue({ year: Number(revYear) })
      : dashboardService.adminRevenue({ year: Number(revYear), city_id: cityId ?? undefined })
    const cityParam = isSchoolAdmin ? {} : { city_id: cityId ?? undefined }
    const trainerP = dashboardService.trainerInsights(cityParam)
    const studentP = dashboardService.studentInsights(cityParam)
    setInsightsLoading(true)
    Promise.allSettled([revenueP, trainerP, studentP]).then(() => setInsightsLoading(false))
    revenueP.then(d => setRevenueData((d as any)?.data ?? d)).catch(()=>{})
    trainerP.then(d => setTrainerInsights((d as any)?.data ?? d)).catch(()=>{})
    studentP.then(d => setStudentInsights((d as any)?.data ?? d)).catch(()=>{})
  }, [revYear, isSchoolAdmin, cityId])

  // Load cities for the top filter (admin only)
  useEffect(() => {
    if (isSchoolAdmin) return
    import('@/api/services/systemService').then(({systemService}) =>
      systemService.cities.list({ limit: 1000 }).then((d:any) => setCities(d?.data ?? [])).catch(()=>{})
    )
  }, [isSchoolAdmin])

  const analytics: Record<string, any> = (analyticsRaw as any)?.data ?? analyticsRaw ?? {}
  // Server already filters to pending requests via is_verified=0.
  const verifications: any[] = (verifyRaw as any)?.data ?? []

  // Accept / reject a pending verification request — runs after modal confirmation.
  // Backend expects `verify` as a boolean (true = accept, false = reject).
  const confirmVerify = useCallback(async () => {
    if (!verifyTarget) return
    const { request, accept } = verifyTarget
    const userId = request?.user_id ?? request?.user?.id
    setVerifyingId({ id: request?.id, action: accept ? 'accept' : 'reject' })
    try {
      // Goes through user-update-by-id so it isn't blocked by the school-verified pending rule.
      const fd = new FormData()
      fd.append('user_id', String(userId))
      fd.append('is_verified', accept ? '1' : '0')
      await restClient.post(endpoints.users.update, fd)
      toast.success(accept ? 'Trainer verified successfully' : 'Request rejected')
      setVerifyTarget(null)
      await refetchVerifications()
    } catch (e: any) {
      const errs = e?.response?.data?.errors
      const msg = errs
        ? (typeof errs === 'string' ? errs : Object.values(errs).flat().join(', '))
        : (e?.response?.data?.message ?? 'Action failed')
      toast.error(msg)
    } finally {
      setVerifyingId(null)
    }
  }, [refetchVerifications, verifyTarget])

  // Revenue chart data — currentYear/lastYear live under revenueData.revenue, not
  // at the top level (confirmed against the real admin/school-analytics payload).
  const revenueChartData = months.map((m, i) => ({
    month: m,
    current: Array.isArray(revenueData?.revenue?.currentYear) ? (Object.values(revenueData.revenue.currentYear[i] ?? {})[0] ?? 0) : 0,
    last: Array.isArray(revenueData?.revenue?.lastYear) ? (Object.values(revenueData.revenue.lastYear[i] ?? {})[0] ?? 0) : 0,
  }))

  // Booking status donut
  const bookingDonut = [
    { name: 'Completed',  value: analytics?.completedBookingsCount  ?? 0, color: '#22c55e' },
    { name: 'Unpaid',     value: analytics?.pendingBookingsCount     ?? 0, color: '#f59e0b' },
    { name: 'Cancelled',  value: analytics?.canceledBookingsCount    ?? 0, color: '#ef4444' },
    { name: 'Confirmed',  value: analytics?.confirmedBookingsCount   ?? 0, color: '#3b82f6' },
  ]

  // Trainer analytics pie
  const trainerPie = [
    { name: t('dash.activeTrainers'),   value: analytics?.activeTrainers   ?? trainerInsights?.activeTrainers   ?? 0, color: '#34C38F' },
    { name: t('dash.inactiveTrainers'), value: analytics?.inactiveTrainers ?? trainerInsights?.inactiveTrainers ?? 0, color: '#FF7D1E' },
  ]

  // Student analytics pie (admin only — school admin no longer shows this card)
  const studentPie = [
    { name: t('dash.activeStudents'),   value: analytics?.activeStudents   ?? 0, color: '#FF6F61' },
    { name: t('dash.inactiveStudents'), value: analytics?.inactiveStudents ?? 0, color: '#6B5B95' },
  ]

  // Enrollment trends chart — the API returns entries spanning multiple years
  // (e.g. month 7 shows up for both last year and this year), so matching on
  // month alone would silently pick whichever year comes first. Scope to the
  // current calendar year, matching the Yearly Revenue chart's own convention.
  const thisYear = new Date().getFullYear()
  const enrollmentData = months.map((m, i) => {
    const enrolled  = studentInsights?.enrollmentTrends?.find((d:any) => d.month === i+1 && d.year === thisYear)?.total_students ?? 0
    const registered = studentInsights?.enrollmentTrendsRegisteredStudents?.find((d:any) => d.month === i+1 && d.year === thisYear)?.total_students ?? 0
    return { month: m, 'Students with Bookings': enrolled, 'Registered Students': registered }
  })

  // Trainer sessions — ranked by total sessions, or by a single selected month.
  // (sessions[] entries carry no `year`, so month numbers are matched as-is —
  // same granularity the API itself gives us here.)
  const sessionsPerTrainerRaw: any[] = trainerInsights?.sessionsPerTrainer ?? []
  const sessionsAvailableMonths = Array.from(
    new Set(sessionsPerTrainerRaw.flatMap((t:any) => (t.sessions ?? []).map((s:any) => Number(s.month))))
  ).sort((a,b) => a-b)
  const trainerSessionsRanked = sessionsPerTrainerRaw
    .map((t:any) => {
      const sessions = t.sessions ?? []
      const totalSessions = sessionsMonthFilter === 'all'
        ? sessions.reduce((s:number, x:any) => s + (Number(x.session_count) || 0), 0)
        : Number(sessions.find((x:any) => Number(x.month) === Number(sessionsMonthFilter))?.session_count) || 0
      return { trainer_id: t.trainer_id, trainer_name: t.trainer_name, trainer_email: t.trainer_email, totalSessions }
    })
    .filter((t:any) => t.totalSessions > 0)
    .sort((a,b) => b.totalSessions - a.totalSessions)
  // Chart reads top to bottom in series order, but a ranking bar chart should
  // show #1 at the top — reverse so ApexCharts' bottom-up bar order comes out right.
  const trainerSessionsTop10 = trainerSessionsRanked.slice(0, 10).reverse()

  // Top trainers list
  const topTrainers: any[] = analytics?.topTrendingTrainers ?? []

  // Top packages list
  const topPackages: any[] = analytics?.mostBookedPackages ?? []

  // School performance
  const schoolsPerformance: any[] = analytics?.schoolsPerformance ?? []

  // Payment methods pie — guard against non-array shapes (school revenue returns an object)
  const paymentSrc = revenueData?.paymentMethods ?? revenueData
  const paymentPie = (Array.isArray(paymentSrc) ? paymentSrc : [])
    .filter((p:any) => p && typeof p === 'object')
    .map((p:any, i:number) => ({
      name: p.payment_method !== undefined && p.payment_method !== null
        ? t(`dash.pm.${getPaymentMethodLabel(p.payment_method)}`, getPaymentMethodLabel(p.payment_method))
        : (p.method ?? p.name ?? t('dash.methodN', { n: i+1 })),
      value: parseFloat(p.total_amount ?? p.amount ?? p.total ?? '0'),
      color: ['#7a4ec9','#fb7c63','#ffbe57','#5dc7e1','#59bb90'][i % 5],
    }))
    .filter((p:any) => Number(p.value) > 0)   // drop empty/zero channels

  // Trainer feedback list
  const trainerFeedback: any[] = trainerInsights?.sessionFeedback ?? []

  // Student demographics
  const demographics = studentInsights?.studentsDemographics ?? {}
  const demoCategories = Object.keys(demographics)
  const demoChartData = demoCategories.map(cat => {
    const row: Record<string,unknown> = { category: cat }
    Object.entries(demographics[cat] ?? {}).forEach(([k,v]) => { row[k] = v })
    return row
  })
  // Union of every subcategory key across all categories (not just the first row).
  const demoKeys = Array.from(
    demoChartData.reduce((set, row) => {
      Object.keys(row).forEach(k => { if (k !== 'category') set.add(k) })
      return set
    }, new Set<string>())
  )

  // Certificates & Sessions chart (bar with period toggle)
  const certMonthly: any[] = analytics?.monthlyIssuedCertificates ?? []
  const certYearly:  any[] = analytics?.yearlyIssuedCertificates ?? []
  const certWeekly:  any[] = analytics?.weeklyIssuedCertificates ?? []
  const sessMonthly: any[] = analytics?.monthlyCompletedSessions ?? []
  const sessYearly:  any[] = analytics?.yearlyCompletedSessions ?? []
  const sessWeekly:  any[] = analytics?.weeklyCompletedSessions ?? []

  // Monthly/Weekly entries span multiple years (e.g. week 3 shows up for both
  // last year and this year) — scope both to the current year so a lookup by
  // month/week alone can't silently pick up the wrong year's number.
  const buildPeriodData = (monthly:any[], yearly:any[], weekly:any[], period:string) => {
    if (period === 'Monthly') return months.map((m,i)=>({ label:m, value: monthly.find((d:any)=>d.month===i+1 && d.year===thisYear)?.total ?? 0 }))
    if (period === 'Weekly') return Array.from({length:12},(_,i)=>({ label:`W${i+1}`, value: weekly.find((d:any)=>d.week===i+1 && d.year===thisYear)?.total ?? 0 }))
    // Yearly
    const years = [...new Set(yearly.map((d:any)=>d.year))].sort() as number[]
    return years.map(y => ({ label: String(y), value: yearly.filter((d:any)=>d.year===y).reduce((s:number,d:any)=>s+d.total,0) }))
  }

  const certChartData = buildPeriodData(certMonthly, certYearly, certWeekly, certSeries)
  const sessChartData = buildPeriodData(sessMonthly, sessYearly, sessWeekly, sessionSeries)

  // Date filter applies instantly — presets and custom range share this setter
  const applyRange = (start: string, end: string) => { setStartDate(start); setEndDate(end) }
  const toYmd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  const datePresets = (() => {
    const now = new Date()
    const daysAgo = (n: number) => new Date(now.getFullYear(), now.getMonth(), now.getDate() - n)
    return [
      { key: 'today',     label: t('dash.today', 'Today'),            start: toYmd(now),       end: toYmd(now) },
      { key: 'yesterday', label: t('dash.yesterday', 'Yesterday'),    start: toYmd(daysAgo(1)), end: toYmd(daysAgo(1)) },
      { key: 'last7',     label: t('dash.last7Days', 'Last 7 Days'),  start: toYmd(daysAgo(6)), end: toYmd(now) },
      { key: 'last30',    label: t('dash.last30Days', 'Last 30 Days'), start: toYmd(daysAgo(29)), end: toYmd(now) },
      { key: 'thisMonth', label: t('dash.thisMonth', 'This Month'),   start: toYmd(new Date(now.getFullYear(), now.getMonth(), 1)), end: toYmd(now) },
      { key: 'lastMonth', label: t('dash.lastMonth', 'Last Month'),   start: toYmd(new Date(now.getFullYear(), now.getMonth()-1, 1)), end: toYmd(new Date(now.getFullYear(), now.getMonth(), 0)) },
    ]
  })()

  const filtering = insightsLoading || (isSchoolAdmin ? schoolStatsQ.isFetching : adminStatsQ.isFetching)

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner className="w-10 h-10"/>
      </div>
    )
  }

  // ── KPI CARDS config ──────────────────────────────────────────────────────
  // School admins must stay inside /school/* — never link them to admin routes.
  const bookingLink = isSchoolAdmin ? '/school/booking/list' : '/dashboard/booking/list'
  const kpiCards = [
    {
      link: bookingLink,
      gradient: 'linear-gradient(to right, #E7F9FF 0%, rgba(231,249,255,0.1) 100%)',
      iconGradient: 'linear-gradient(135deg, #4dd4e8 0%, #36b5c7 100%)',
      value: fmt(analytics?.revenueGenerated) || '—',
      exactValue: analytics?.revenueGenerated,
      label: t('dash.totalRevenue'),
      perm: 'booking_school_admin',
      icon: '/assets/school-icons/1.svg',
    },
    {
      link: bookingLink,
      gradient: 'linear-gradient(to right, #E6EBFF 0%, rgba(230,235,255,0.1) 100%)',
      iconGradient: 'linear-gradient(135deg, #5865f2 0%, #4651d9 100%)',
      value: analytics?.bookingsCount ?? '—',
      label: t('dash.totalBooking'),
      perm: 'booking_school_admin',
      hoverLines: [
        { label: t('dash.pendingBookings'),    value: analytics?.pendingBookingsCount    ?? 0 },
        { label: t('dash.inProgressBookings'), value: analytics?.inProgressBookingsCount ?? 0 },
        { label: t('dash.confirmedBookings'),  value: analytics?.confirmedBookingsCount  ?? 0 },
        { label: t('dash.completedBookings'),  value: analytics?.completedBookingsCount  ?? 0 },
        { label: t('dash.cancelledBookings'),  value: analytics?.canceledBookingsCount   ?? 0 },
      ],
      icon: '/assets/school-icons/3.svg',
    },
    {
      link: bookingLink,
      gradient: 'linear-gradient(to right, #E9FFF5 0%, rgba(233,255,245,0.1) 100%)',
      iconGradient: 'linear-gradient(135deg, #00e676 0%, #00c95c 100%)',
      value: analytics?.inProgressBookingsCount ?? analytics?.pendingBookingsCount ?? '—',
      label: t('dash.inProgressBooking'),
      perm: 'booking_school_admin',
      icon: '/assets/school-icons/2.svg',
    },
    {
      link: isSchoolAdmin ? '/school/approved-certificate' : '/dashboard/approved-certificate',
      gradient: 'linear-gradient(to right, #FFEFEF 0%, rgba(255,239,239,0.1) 100%)',
      iconGradient: 'linear-gradient(135deg, #ff5252 0%, #e63939 100%)',
      value: analytics?.issuedCertificates ?? '—',
      label: t('dash.issuedCertificates'),
      perm: 'certificate_school_admin',
      icon: '/assets/school-icons/4.svg',
    },
  ]

  // ── Analytics filter cards ─────────────────────────────────────────────
  const analyticsCards = [
    { link: isSchoolAdmin?'/school/trainers-list':'/dashboard/user/list', linkState: isSchoolAdmin ? undefined : { tab: 'TRAINER' }, bgColor:'#E3F2FD', value: trainerInsights ? (trainerInsights.activeTrainers ?? 0) + (trainerInsights.inactiveTrainers ?? 0) : (analytics?.trainerCount ?? '0'), label:t('dash.totalTrainers'), perm: 'trainer_list_school_admin',
      hoverLines: trainerInsights ? [
        { label: t('dash.activeTrainers'),   value: trainerInsights.activeTrainers   ?? 0 },
        { label: t('dash.inactiveTrainers'), value: trainerInsights.inactiveTrainers ?? 0 },
        { label: t('dash.maleTrainers'),     value: trainerInsights.maleTrainers     ?? 0 },
        { label: t('dash.femaleTrainers'),   value: trainerInsights.femaleTrainers   ?? 0 },
      ] : undefined,
      icon:<TrainersIcon className="w-7 h-7 text-[#1e88e5]"/> },
    { link: isSchoolAdmin?'/school/report/student':'/dashboard/user/list', linkState: isSchoolAdmin ? undefined : { tab: 'STUDENT' }, bgColor:'#E8F5E9', value: analytics?.studentCount ?? '0', label:t('dash.totalStudents'), perm: 'report_school_admin', hoverLines: [{ label: t('dash.withBookings'), value: studentInsights?.studentsWithBookingsCount ?? 0 }],      icon:<StudentsIcon className="w-7 h-7 text-[#2e9e5b]"/> },
    { link: bookingLink, bgColor:'#FFEBEE', value: analytics?.failedTransactions ?? '0', label:t('dash.failedTransactions'), perm: 'booking_school_admin',  icon:<FailedTransactionIcon className="w-7 h-7 text-[#e53935]"/> },
    { link: isSchoolAdmin?'/school/awaiting-certificate':'/dashboard/awaiting-certificate', bgColor:'#E1F5FE', value: analytics?.pendingCertificates ?? '0', label:t('dash.pendingCertificates'), perm: 'certificate_school_admin', icon:<PendingCertificateIcon className="w-7 h-7 text-[#0288d1]"/> },
    { link: bookingLink, bgColor:'#FFF3E0', value: analytics?.rescheduledBookingsCount ?? '0', label:t('dash.rescheduledBookings'), perm: 'booking_school_admin', icon:<RescheduledBookingIcon className="w-7 h-7 text-[#e67e22]"/> },
    ...(!isSchoolAdmin ? [
      { link: '/dashboard/school/list', bgColor:'#FFF9C4', value: analytics?.schoolCount ?? '0', label:t('dash.totalSchools'), icon:<SchoolsIcon className="w-7 h-7 text-[#b7950b]"/> },
      { link: '/dashboard/booking/refund', bgColor:'#E0F7FA', value: analytics?.refundReqs ?? '0', label:t('dash.refundRequests'), icon:<RefundIcon className="w-7 h-7 text-[#00838f]"/> },
    ] : []),
  ]

  // Shared between two layout slots: paired beside Trainer Analytics for school
  // admin (nothing else to pair it with there), full-width on its own for admin.
  const trainerFeedbackCard = (
    <CardShell
      title={t('dash.trainerFeedback')}
      subheader={t('dash.latestSessionRatings')}
      action={trainerFeedback.length > 0 && (
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
          {trainerFeedback.length}
        </span>
      )}
    >
      {trainerFeedback.length > 0 ? (
        <div className="-mx-5 max-h-80 overflow-y-auto overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50 sticky top-0 z-10"><tr><th>{t('dash.trainer')}</th><th>Email</th><th>Feedback</th><th>Rating</th><th>Score</th></tr></thead>
            <tbody>
              {trainerFeedback.map((f:any, i:number) => {
                const rating = parseFloat(f.avg_rating ?? '0') || 0
                const comment = cleanFeedbackComments(f.feedback_comments)
                return (
                  <tr key={i}>
                    <td>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar name={f.trainer_name ?? 'Trainer'} src={f.photo_url ?? f.trainer_photo} size="sm"/>
                        <span className="truncate font-semibold text-gray-900">{f.trainer_name ?? '—'}</span>
                      </div>
                    </td>
                    <td className="text-gray-500">{f.trainer_email ?? '—'}</td>
                    <td className="max-w-xs">
                      {comment
                        ? <p className="line-clamp-2 text-gray-600 leading-relaxed" title={comment}>{comment}</p>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td><Stars n={rating}/></td>
                    <td>
                      <span className={`inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold ${
                        f.avg_rating ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-400'
                      }`}>
                        <Star size={12} className={f.avg_rating ? 'fill-brand-400 text-brand-400' : ''}/>
                        {f.avg_rating ? rating.toFixed(1) : t('dash.na')}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noFeedback')}</p>}
    </CardShell>
  )

  return (
    <div className="space-y-6 pb-8">
      <h1 className="font-semibold text-black text-[1.75rem] sm:text-[2.5rem] md:text-5xl mb-2">{t('dash.dashboard')}</h1>

      {/* ── Filters: city + period (both apply instantly) ── */}
      {(() => {
        const palette = [
          { bg: 'linear-gradient(to right, #E7F9FF 0%, rgba(231,249,255,0.1) 100%)', icon: 'linear-gradient(135deg, #4dd4e8 0%, #36b5c7 100%)', ring: '#36b5c7' },
          { bg: 'linear-gradient(to right, #E6EBFF 0%, rgba(230,235,255,0.1) 100%)', icon: 'linear-gradient(135deg, #5865f2 0%, #4651d9 100%)', ring: '#4651d9' },
          { bg: 'linear-gradient(to right, #E9FFF5 0%, rgba(233,255,245,0.1) 100%)', icon: 'linear-gradient(135deg, #00e676 0%, #00c95c 100%)', ring: '#00c95c' },
          { bg: 'linear-gradient(to right, #FFEFEF 0%, rgba(255,239,239,0.1) 100%)', icon: 'linear-gradient(135deg, #ff5252 0%, #e63939 100%)', ring: '#e63939' },
          { bg: 'linear-gradient(to right, #FFF3E0 0%, rgba(255,243,224,0.1) 100%)', icon: 'linear-gradient(135deg, #ffa24d 0%, #f68b2c 100%)', ring: '#f68b2c' },
        ]
        const FilterCard = ({ active, tone, title, onClick }: {
          active: boolean; tone: typeof palette[number]; title: string; onClick: () => void
        }) => (
          <button onClick={onClick}
            className="relative flex items-center justify-center rounded-lg px-3 py-2 text-center transition-all hover:opacity-90"
            style={{ background: tone.bg, boxShadow: active ? `0 0 0 2px ${tone.ring}, 0 4px 12px -4px ${tone.ring}66` : '0 0 0 1px rgba(0,0,0,0.04)' }}>
            <span className="min-w-0">
              <span className="block text-xs font-bold text-gray-900 uppercase tracking-wide leading-tight truncate">{title}</span>
            </span>
            {active && (
              <span className="absolute top-1 right-1 w-4 h-4 rounded-full flex items-center justify-center text-white" style={{ background: tone.ring }}>
                {filtering ? <span className="w-2.5 h-2.5 rounded-full border-2 border-white/40 border-t-white animate-spin"/> : <Check size={10} strokeWidth={3}/>}
              </span>
            )}
          </button>
        )
        return (
          <div className="space-y-4">
            {!isSchoolAdmin && cities.length > 0 && (
              <Card className="!p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold text-gray-900">{t('dash.selectCity')}</h2>
                  {cityId !== null && (
                    <button onClick={() => setCityId(null)} className="text-xs font-semibold hover:underline" style={{ color:'#f68b2c' }}>{t('dash.clear')}</button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                  <FilterCard active={cityId === null} tone={palette[4]}
                    title={t('dash.allCities', 'All Cities')} onClick={() => setCityId(null)} />
                  {cities.map((c:any, i:number) => (
                    <FilterCard key={c.id} active={cityId === c.id} tone={palette[i % 4]}
                      title={c.city_translations?.map((x:any)=>x.name).join(' - ') || c.name} onClick={() => setCityId(c.id)} />
                  ))}
                </div>
              </Card>
            )}

            <Card className="!p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-gray-900">{t('dash.selectDate')}</h2>
                {(startDate || endDate) && (
                  <button onClick={() => applyRange('', '')} className="text-xs font-semibold hover:underline" style={{ color:'#f68b2c' }}>{t('dash.clear')}</button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
                {datePresets.map((p, i) => (
                  <FilterCard key={p.key} active={startDate === p.start && endDate === p.end} tone={palette[i % 4]}
                    title={p.label}
                    onClick={() => applyRange(p.start, p.end)} />
                ))}
                <div className="rounded-lg px-2 py-1.5 bg-white flex items-center gap-1.5 col-span-2 md:col-span-4 xl:col-span-2"
                  style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}>
                  <input type="date" value={startDate} max={endDate || undefined} onChange={e => applyRange(e.target.value, endDate || e.target.value)}
                    className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                  <input type="date" value={endDate} min={startDate || undefined} onChange={e => applyRange(startDate || e.target.value, e.target.value)}
                    className="flex-1 text-xs border border-gray-200 rounded-md px-2 py-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-brand-400"/>
                </div>
              </div>
            </Card>
          </div>
        )
      })()}

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {kpiCards.filter(c => schoolCan(c.perm)).map(({ perm: _perm, ...c }, i) => <DashboardCard key={i} {...c}/>)}
      </div>

      {/* ── Analytics Filter ── */}
      <Card>
        <SectionHeader title={t('dash.analyticsFilter')} />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {analyticsCards.filter(c => schoolCan(c.perm)).map(({ perm: _perm, ...c }, i) => <AnalyticsCard key={i} {...c}/>)}
        </div>
      </Card>

      {/* ── Payment Revenue + (Pending Verifications for admin / Upcoming Schedules for school) ── */}
      <div className={`grid grid-cols-1 gap-5 ${isSchoolAdmin ? 'lg:grid-cols-2' : 'lg:grid-cols-5'}`}>
        {/* Payment Methods Revenue (Polar area) — half width beside the calendar for school admin */}
        {schoolCan('report_school_admin') && <Card className={isSchoolAdmin ? '' : 'lg:col-span-3'}>
          {paymentPie.length > 0 ? (
            <PaymentMethodRevenue
              title={t('dash.paymentMethodsRevenue')}
              subheader={t('dash.revenueByChannel')}
              series={paymentPie.map((p:any) => ({ label: p.name, value: p.value }))}
              colors={paymentPie.map((p:any) => p.color)}
            />
          ) : (
            <>
              <div className="mb-1 font-bold text-gray-900">{t('dash.paymentMethodsRevenue')}</div>
              <p className="text-xs text-gray-400 mb-4">{t('dash.revenueByChannel')}</p>
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 text-gray-300"><Wallet size={22}/></div>
                <p className="text-sm font-medium text-gray-500">{t('dash.noPaymentRevenue')}</p>
                <p className="text-xs text-gray-400">{t('dash.noPaymentRevenueHint')}</p>
              </div>
            </>
          )}
        </Card>}

        {/* Upcoming Schedules calendar — school admin only, sits beside payment revenue */}
        {isSchoolAdmin && schoolCan('booking_school_admin') && <UpcomingSchedules source="schoolAdmin" />}

        {/* Pending Verification Requests — admin only */}
        {!isSchoolAdmin && (
        <CardShell
          title="Pending Verification Request"
          subheader="Requests awaiting your review"
          className="lg:col-span-2"
          action={
            verifications.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-600">
                {verifications.length} pending
              </span>
            )
          }
        >
          {verifyLoading ? (
            <div className="flex justify-center py-8"><Spinner/></div>
          ) : verifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-500">
                <Check size={18}/>
              </div>
              <p className="text-sm text-gray-400">No pending verifications</p>
            </div>
          ) : (
            <div className="-mx-2 max-h-64 space-y-1 overflow-y-auto pr-1">
              {verifications.map((p:any, i:number) => (
                <div
                  key={p.id??i}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-gray-50"
                >
                  <Avatar name={p.user?.name ?? p.name ?? 'User'} src={p.user?.photo_url} size="md"/>
                  {(() => {
                    const vt = p.vendor?.translations ?? p.vendor?.vendor_translations
                    const schoolName = vt?.find((t:any)=>t?.locale?.toLowerCase()==='en')?.name
                      ?? vt?.[0]?.name
                      ?? p.school_name ?? p.description ?? null
                    const reqTime = p.requested_time ?? p.created_at
                    return (
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-gray-900">{p.user?.name ?? p.name ?? 'Unknown'}</div>
                    {schoolName && (
                      <div className="truncate flex items-center gap-1 text-xs text-brand-500"><Building2 size={11} className="flex-shrink-0"/>{schoolName}</div>
                    )}
                    {reqTime && (
                      <div className="mt-0.5 flex items-center gap-1 text-[13px] text-gray-400">
                        <Clock size={11}/>
                        {new Date(reqTime).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).replace(',','')}
                      </div>
                    )}
                  </div>
                    )
                  })()}
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      aria-label="Reject request"
                      disabled={!!verifyingId}
                      onClick={() => setVerifyTarget({ request: p, accept: false })}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {verifyingId && verifyingId.id === p.id && verifyingId.action === 'reject'
                        ? <Spinner className="w-3.5 h-3.5"/>
                        : <X size={14}/>}
                      Reject
                    </button>
                    <button
                      type="button"
                      aria-label="Accept request"
                      disabled={!!verifyingId}
                      onClick={() => setVerifyTarget({ request: p, accept: true })}
                      className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {verifyingId && verifyingId.id === p.id && verifyingId.action === 'accept'
                        ? <Spinner className="w-3.5 h-3.5 text-white"/>
                        : <Check size={14}/>}
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardShell>
        )}
      </div>

      {/* ── Booking Session Requests (school admin only) ── */}
      {isSchoolAdmin && schoolCan('booking_school_admin') && (
        <CardShell
          title={t('dash.bookingSessionRequests')}
          subheader={t('dash.recentSessionRequests')}
          action={
            <div className="flex items-center gap-2">
              {sessionRequestsTotal > 0 && (
                <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-600">
                  {sessionRequestsTotal} {t('dash.total')}
                </span>
              )}
              <button onClick={() => navigate('/school/booking/session-requests')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline">
                {t('dash.viewAll')} <ArrowRight size={12} className="rtl:rotate-180"/>
              </button>
            </div>
          }
        >
          {sessionRequestsQ.isLoading ? (
            <div className="flex justify-center py-8"><Spinner/></div>
          ) : sessionRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 text-gray-300">
                <CalendarClock size={18}/>
              </div>
              <p className="text-sm text-gray-400">{t('dash.noSessionRequests')}</p>
            </div>
          ) : (
            <>
            <div className="-mx-5 max-h-80 overflow-y-auto overflow-x-auto">
              <table className="drivys-table w-full">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th>{t('dash.student')}</th>
                    <th>{t('dash.trainer')}</th>
                    <th>{t('dash.requestedTime')}</th>
                    <th>{t('dash.status')}</th>
                    <th className="text-right">{t('dash.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionRequests.map((r: any) => {
                    const student = r.booking?.user ?? {}
                    const trainer = r.driver_details ?? r.booking?.driver ?? {}
                    const status  = (r.session_status ?? 'PENDING').toString()
                    const isPending = status.toUpperCase() === 'PENDING'
                    return (
                      <tr key={r.id}>
                        <td>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Avatar name={student.name ?? 'Student'} src={student.photo_url} size="sm"/>
                            <span className="truncate font-semibold text-gray-900">{student.name ?? '—'}</span>
                          </div>
                        </td>
                        <td className="text-gray-600">{trainer.name ?? '—'}</td>
                        <td className="text-gray-600 whitespace-nowrap">
                          {r.start_time ? (
                            <span className="flex items-center gap-1">
                              <Clock size={11} className="text-gray-400"/>
                              {new Date(r.start_time).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).replace(',','')}
                            </span>
                          ) : '—'}
                        </td>
                        <td><StatusBadge status={status}/></td>
                        <td className="text-right">
                          {isPending ? (
                            <div className="flex justify-end items-center gap-2">
                              <button
                                type="button"
                                onClick={() => navigate('/school/booking/session-requests')}
                                className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100"
                              >
                                <X size={12}/> {t('dash.reject')}
                              </button>
                              <button
                                type="button"
                                disabled={acceptingSessionId === r.id}
                                onClick={() => handleAcceptSessionRequest(r.id)}
                                className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {acceptingSessionId === r.id ? <Spinner className="w-3.5 h-3.5 text-white"/> : <Check size={12}/>}
                                {t('dash.accept')}
                              </button>
                            </div>
                          ) : <span className="text-xs text-gray-300">—</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {sessionReqLastPage > 1 && (
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-gray-400">
                  {(sessionReqPage - 1) * sessionReqPerPage + 1}–{Math.min(sessionReqPage * sessionReqPerPage, sessionRequestsTotal)} of {sessionRequestsTotal}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setSessionReqPage(p => Math.max(1, p - 1))}
                    disabled={sessionReqPage <= 1}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronLeft size={14}/>
                  </button>
                  {Array.from({ length: sessionReqLastPage }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - sessionReqPage) <= 2 || p === 1 || p === sessionReqLastPage)
                    .map((p, idx, arr) => (
                      <span key={p} className="flex items-center">
                        {idx > 0 && p - arr[idx - 1] > 1 && <span className="px-1 text-xs text-gray-300">…</span>}
                        <button
                          onClick={() => setSessionReqPage(p)}
                          className={`w-8 h-8 rounded-lg border text-xs font-semibold ${p === sessionReqPage ? 'bg-brand-500 text-white border-brand-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                  <button
                    onClick={() => setSessionReqPage(p => Math.min(sessionReqLastPage, p + 1))}
                    disabled={sessionReqPage >= sessionReqLastPage}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronRight size={14}/>
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </CardShell>
      )}

      {/* ── Heat Map — admin sees trainers + students, school admin sees their trainers only ── */}
      {(user?.user_type === 'admin' || (isSchoolAdmin && schoolCan('trainer_list_school_admin'))) && <HeatMap />}

      {/* ── Trainer Analytics + (Upcoming Schedules for admin / Trainer Feedback for school admin) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {schoolCan('trainer_list_school_admin') && (
        <CardShell title={t('dash.trainerAnalytics')} subheader={t('dash.totalTrainersN', { n: trainerInsights ? (trainerInsights.activeTrainers ?? 0) + (trainerInsights.inactiveTrainers ?? 0) : (analytics?.trainerCount ?? 0) })}>
          {trainerPie.some(d=>d.value>0) ? (
            <AnalyticsBreakdown series={trainerPie.map(d=>({label:d.name,value:d.value,color:d.color}))} totalLabel={t('dash.totalTrainers')}/>
          ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noTrainerData')}</p>}
        </CardShell>
        )}

        {isSchoolAdmin ? (schoolCan('review_school_admin') && trainerFeedbackCard) : <UpcomingSchedules source="admin" />}
      </div>

      {/* ── Student Analytics + School Performance (admin only) ── */}
      {!isSchoolAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <CardShell title="Student Analytics" subheader={`Total Students: ${analytics?.studentCount ?? 0}`}>
            {studentPie.some(d=>d.value>0) ? (
              <AnalyticsBreakdown series={studentPie.map(d=>({label:d.name,value:d.value,color:d.color}))} totalLabel="Students"/>
            ) : <p className="text-sm text-gray-400 text-center py-10">No student data</p>}
          </CardShell>

          {schoolsPerformance.length > 0 && (
            <SchoolPerformanceCard data={schoolsPerformance}/>
          )}
        </div>
      )}

      {/* ── Enrollment Trends (full) ── */}
      {!isSchoolAdmin && (() => {
        const bookingsSeries   = enrollmentData.map(d=>Number(d['Students with Bookings'])||0)
        const registeredSeries = enrollmentData.map(d=>Number(d['Registered Students'])||0)
        const totalBookings    = bookingsSeries.reduce((s,v)=>s+v,0)
        const totalRegistered  = registeredSeries.reduce((s,v)=>s+v,0)
        return (
          <CardShell
            title="Enrollment Trends"
            subheader="Students with bookings vs registered students per month"
            action={
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor:'#ffab00' }}/>
                  <div className="leading-tight">
                    <div className="text-sm font-bold text-gray-900">{totalBookings.toLocaleString()}</div>
                    <div className="text-[13px] text-gray-400">With Bookings</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor:'#0da670' }}/>
                  <div className="leading-tight">
                    <div className="text-sm font-bold text-gray-900">{totalRegistered.toLocaleString()}</div>
                    <div className="text-[13px] text-gray-400">Registered</div>
                  </div>
                </div>
              </div>
            }
          >
            <AreaEnrollmentChart
              categories={months}
              colors={['#ffab00','#0da670']}
              series={[
                { name:'Students with Bookings', data: bookingsSeries },
                { name:'Registered Students',    data: registeredSeries },
              ]}
            />
          </CardShell>
        )
      })()}

      {/* ── Student Category Preferences (full) ── */}
      {demoChartData.length > 0 && schoolCan('report_school_admin') && (
        <CardShell title={t('dash.studentCategoryPreferences')} subheader={t('dash.breakdownByCategory')}>
          <BarDemographicsChart
            categories={demoCategories.map(humanize)}
            series={demoKeys.map(k => ({ name: humanize(k), data: demoChartData.map((r:any)=>Number(r[k])||0) }))}
          />
        </CardShell>
      )}

      {/* ── Trainer Feedback (school admin gets this paired with Trainer Analytics above instead) ── */}
      {!isSchoolAdmin && <div>{trainerFeedbackCard}</div>}

      {/* ── Trainer Sessions + Top Trainers (admin only — school admin gets Trainer Sessions full-width) ── */}
      <div className={`grid grid-cols-1 gap-5 ${isSchoolAdmin ? '' : 'md:grid-cols-2'}`}>
        {schoolCan('trainer_list_school_admin') && <CardShell
          title={t('dash.trainerSessions')}
          subheader={sessionsMonthFilter === 'all' ? t('dash.monthlySessionsPerTrainer') : `Ranked by sessions in ${months[Number(sessionsMonthFilter)-1] ?? ''}`}
          action={
            <div className="flex items-center gap-2">
              {sessionsAvailableMonths.length > 0 && (
                <>
                  <select value={sessionsMonthFilter} onChange={e=>setSessionsMonthFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-400">
                    <option value="all">All months</option>
                    {sessionsAvailableMonths.map(m => <option key={m} value={m}>{months[m-1] ?? `Month ${m}`}</option>)}
                  </select>
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                    {trainerSessionsRanked.length}
                  </span>
                </>
              )}
              <button
                onClick={() => navigate(isSchoolAdmin ? '/school/trainer-sessions' : '/dashboard/trainer-sessions')}
                className="text-xs font-semibold text-brand-600 hover:underline whitespace-nowrap"
              >
                Show All
              </button>
            </div>
          }
        >
          {trainerSessionsRanked.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              <BarRankingChart
                categories={trainerSessionsTop10.map((tr:any) => tr.trainer_name ?? '—')}
                data={trainerSessionsTop10.map((tr:any) => tr.totalSessions)}
                name="Sessions"
                unit="sessions"
                color={PALETTE.primary.main}
              />
            </div>
          ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noSessionData')}</p>}
        </CardShell>}

        {!isSchoolAdmin && (
        <CardShell title={t('dash.topTrainers')} subheader={t('dash.mostBookedTrainers')}>
          {topTrainers.length > 0 ? (
            <div className="-mx-5 max-h-80 overflow-y-auto overflow-x-auto">
              <table className="drivys-table w-full">
                <thead className="bg-gray-50 sticky top-0 z-10"><tr><th>#</th><th>{t('dash.trainer')}</th><th>Email</th><th className="text-right">{t('dash.bookings')}</th></tr></thead>
                <tbody>
                  {topTrainers.map((tr:any, i:number) => (
                    <tr key={tr.id??i}>
                      <td className="text-gray-400 font-mono text-xs">{i+1}</td>
                      <td>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Avatar name={tr.name??'T'} src={tr.photo_url} size="sm"/>
                          <span className="truncate font-semibold text-gray-900">{tr.name ?? '—'}</span>
                        </div>
                      </td>
                      <td className="text-gray-500">{tr.email ?? '—'}</td>
                      <td className="text-right font-bold text-gray-900">{tr.total_bookings ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noTrainerData')}</p>}
        </CardShell>
        )}
      </div>

      {/* ── Yearly Revenue + Top Packages (admin only) ── */}
      {!isSchoolAdmin && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <CardShell
          title={t('dash.yearlyRevenue')}
          action={
            <select value={revYear} onChange={e => setRevYear(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-400">
              {Array.from({length:5},(_,i)=>new Date().getFullYear()-i).map(y=><option key={y} value={y}>{y}</option>)}
            </select>
          }
        >
          <AreaYearlyChart
            categories={months}
            colors={['#f68b2c','#00B8D9']}
            series={[
              { name:t('dash.currentYearRevenue'), data: revenueChartData.map(d=>Number(d.current)||0) },
              { name:t('dash.lastYearRevenue'),    data: revenueChartData.map(d=>Number(d.last)||0) },
            ]}
          />
        </CardShell>

        <CardShell title="Top Packages" subheader="Most booked packages">
          {topPackages.length > 0 ? (
            <div className="-mx-5 max-h-80 overflow-y-auto overflow-x-auto">
              <table className="drivys-table w-full">
                <thead className="bg-gray-50 sticky top-0 z-10"><tr><th>#</th><th>Package</th><th className="text-right">Bookings</th></tr></thead>
                <tbody>
                  {(() => {
                    const maxBookings = Math.max(...topPackages.map((p:any)=>Number(p.total_bookings)||0), 1)
                    return topPackages.map((p:any, i:number) => {
                      const bookings = Number(p.total_bookings) || 0
                      const pct = Math.round((bookings / maxBookings) * 100)
                      return (
                        <tr key={p.package_id??i}>
                          <td className="text-gray-400 font-mono text-xs">{i+1}</td>
                          <td>
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                                <BookOpen size={14} className="text-brand-600"/>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-gray-900">{p.name ?? '—'}</div>
                                <div className="mt-1 h-1 w-full max-w-[140px] overflow-hidden rounded-full bg-gray-100">
                                  <div className="h-full rounded-full bg-brand-400" style={{ width: `${pct}%` }}/>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-right font-bold text-gray-900">{bookings}</td>
                        </tr>
                      )
                    })
                  })()}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-gray-400 text-center py-10">No package data</p>}
        </CardShell>
      </div>
      )}

      {/* ── Issued Certificates + Completed Sessions (admin only) ── */}
      {!isSchoolAdmin && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <CardShell title={t('dash.issuedCertificates')} action={<TabSelector options={['Weekly','Monthly','Yearly']} value={certSeries} onChange={setCertSeries}/>}>
          {certChartData.length > 0 ? (
            <BarStatisticsChart name={t('dash.issuedCertificates')} colors={['#f68b2c']}
              categories={certChartData.map((d:any)=>d.label)} data={certChartData.map((d:any)=>d.value)}/>
          ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noCertificateData')}</p>}
        </CardShell>

        <CardShell title={t('dash.completedSessions')} action={<TabSelector options={['Weekly','Monthly','Yearly']} value={sessionSeries} onChange={setSessionSeries}/>}>
          {sessChartData.length > 0 ? (
            <BarStatisticsChart name={t('dash.completedSessions')} colors={['#00B8D9']}
              categories={sessChartData.map((d:any)=>d.label)} data={sessChartData.map((d:any)=>d.value)}/>
          ) : <p className="text-sm text-gray-400 text-center py-10">{t('dash.noSessionData')}</p>}
        </CardShell>
      </div>
      )}

      {/* ── Verify / Reject confirmation modal ── */}
      <ConfirmDialog
        open={!!verifyTarget}
        onClose={() => setVerifyTarget(null)}
        onConfirm={confirmVerify}
        title={verifyTarget?.accept ? 'Verify Trainer' : 'Reject Request'}
        description={
          verifyTarget
            ? `${verifyTarget.accept ? 'Verify' : 'Reject'} the request for ${verifyTarget.request?.user?.name ?? verifyTarget.request?.name ?? 'this trainer'}?`
            : ''
        }
        confirmLabel={verifyTarget?.accept ? 'Verify' : 'Reject'}
        variant={verifyTarget?.accept ? 'primary' : 'danger'}
        loading={!!verifyingId}
      />
    </div>
  )
}

// ── School Performance sub-component ──────────────────────────────────────
function PerfTile({ icon, label, value, sub, iconClass }: {
  icon: React.ReactNode; label: string; value: string | number; sub?: React.ReactNode; iconClass: string
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconClass}`}>{icon}</div>
      <div>
        <div className="text-lg font-bold leading-tight text-gray-900">{value}</div>
        <div className="text-xs text-gray-400">{label}</div>
        {sub}
      </div>
    </div>
  )
}

function SchoolPerformanceCard({ data }: { data: any[] }) {
  const [selected, setSelected] = useState(data[0])

  const revenue  = parseFloat(selected?.revenue ?? 0) || 0
  const bookings = Number(selected?.bookings ?? 0) || 0
  const rating   = parseFloat(selected?.trainer_ratings ?? 0) || 0

  return (
    <CardShell
      title="School Performance"
      subheader="Key metrics for the selected school"
      action={
        <select
          value={selected?.school_id ?? ''}
          onChange={e => setSelected(data.find((d:any)=>String(d.school_id)===e.target.value) ?? null)}
          className="max-w-[180px] truncate text-sm border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-400"
        >
          {data.map((d:any) => <option key={d.school_id} value={d.school_id}>{d.school_name}</option>)}
        </select>
      }
    >
      {selected ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <PerfTile
            icon={<Wallet size={18}/>}
            iconClass="bg-brand-50 text-brand-600"
            label="Revenue"
            value={`AED ${revenue.toLocaleString()}`}
          />
          <PerfTile
            icon={<Calendar size={18}/>}
            iconClass="bg-brand-50 text-brand-600"
            label="Bookings"
            value={bookings.toLocaleString()}
          />
          <PerfTile
            icon={<Star size={18}/>}
            iconClass="bg-green-50 text-green-600"
            label="Avg Rating"
            value={rating.toFixed(1)}
            sub={<div className="mt-0.5"><Stars n={rating}/></div>}
          />
        </div>
      ) : <p className="text-sm text-gray-400 text-center py-10">No school data</p>}
    </CardShell>
  )
}
